import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastData {
  id: string
  message: string
  variant: ToastVariant
  duration?: number
}

interface ToastProps {
  toast: ToastData
  onDismiss: (id: string) => void
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-green-600/90 border-green-500/50 text-white',
  error: 'bg-red-600/90 border-red-500/50 text-white',
  warning: 'bg-amber-600/90 border-amber-500/50 text-white',
  info: 'bg-cyan-600/90 border-cyan-500/50 text-white',
}

const variantIcons: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'i',
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true)
  const duration = toast.duration ?? 4000

  useEffect(() => {
    if (duration <= 0) return
    const timer = setTimeout(() => {
      setIsVisible(false)
    }, duration)
    return () => clearTimeout(timer)
  }, [duration])

  const handleAnimationComplete = () => {
    if (!isVisible) {
      onDismiss(toast.id)
    }
  }

  return (
    <AnimatePresence onExitComplete={handleAnimationComplete}>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm shadow-lg
            ${variantStyles[toast.variant]}
          `}
        >
          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-xs font-bold">
            {variantIcons[toast.variant]}
          </span>
          <p className="text-sm font-medium flex-1">{toast.message}</p>
          <button
            onClick={() => setIsVisible(false)}
            className="text-white/70 hover:text-white transition-colors text-lg leading-none"
          >
            ×
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface ToastContainerProps {
  toasts: ToastData[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}
